import { useState, useRef, useEffect } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import { Bot, Send } from "lucide-react";

const SUGGESTIONS = [
  "Which assets should we protect first?",
  "Which facilities have inadequate resilience?",
  "Which projects and revenue could be affected by the current hazards?",
  "What adaptation actions should we prioritise this quarter?",
];

export function ResilienceAgent() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setMessages((p) => [...p, { role: "user", text: question }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/climate-risk/agent", { question });
      setMessages((p) => [...p, { role: "agent", text: data.answer, sources: data.grounding_sources }]);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-4 flex flex-col h-[520px]" data-testid="resilience-agent">
      <div className="flex items-center gap-2 mb-3">
        <Bot className="h-4 w-4 text-accent" />
        <span className="font-semibold text-sm">Own Climate Resilience Agent</span>
        <span className="text-[10px] text-muted-foreground">grounded in your twin, hazards, scores & OCR register</span>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {!messages.length && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Ask about your own operations:</div>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => ask(s)} data-testid="agent-suggestion"
                className="block w-full text-left text-xs border border-border rounded-sm px-3 py-2 hover:bg-surface-hover hover:border-accent/40 transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-xs leading-relaxed whitespace-pre-wrap rounded-sm px-3 py-2 max-w-[92%] ${
            m.role === "user" ? "ml-auto bg-accent/10 border border-accent/30" : "bg-surface border border-border"}`}>
            {m.text}
            {m.sources && (
              <div className="mt-1.5 text-[9px] text-muted-foreground/70 font-mono">Sources: {m.sources.join(" · ")}</div>
            )}
          </div>
        ))}
        {busy && <div className="text-xs text-muted-foreground animate-pulse">Analysing your operations…</div>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="e.g. Which assets should we protect first?" className="text-xs resize-none"
          data-testid="agent-input" />
        <Button variant="primary" size="sm" onClick={() => ask()} disabled={busy || !input.trim()} data-testid="agent-send-btn">
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
